import { Api, ApiError } from "../api";
import { h, label, pill, replace } from "../dom";
import { when } from "../format";
import type { ActResult, Draft, Me, OrderHistory, OutboundLogRow, Page } from "../types";
import { logRow, sentBy } from "../mail";
import { can, confirmButton, crumbs, openPdf, orderCrumbs, outcome } from "./shared";

// One outbound draft: read it, edit it, send it through the outbound guard, or discard it with a reason. The guard
// runs every check again when it is sent; in SIMULATION it goes only to the simulation recipients, marked [SIM].

const list = (s: string) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

export async function draft(api: Api, me: Me, draftId: number): Promise<HTMLElement> {
  const d = (await api.tool<Page<Draft>>("outbound_drafts", { draft_id: draftId })).rows[0];
  // A vendor PO's email: its order, for the breadcrumb and the way back (an email knows only the PO's number).
  const order = d?.vendorPo
    ? await api.tool<OrderHistory>("order_history", { vendor_po: d.vendorPo, limit: 1 }).catch(() => null)
    : null;
  // Sent: when it went and who actually got it (in SIMULATION, the simulation recipients), from the guard's log.
  const sentLog = d?.status === "SENT"
    ? (await api.tool<Page<Partial<OutboundLogRow>>>("outbound_log", { draft_id: d.draftId, limit: 10 })).rows.map(logRow)
        .find((r) => r.decision === "SENT" || r.decision === "REDIRECTED")
    : undefined;
  if (!d) return h("div", { class: "page" }, h("p", { class: "lede" }, "There's no such draft."));
  const cur = d.final ?? { to: d.to, cc: d.cc, subject: d.subject, body: d.body };
  const pending = d.status === "PENDING";
  const canEdit = pending && can(me, "drafts.write");
  const canSend = pending && can(me, "drafts.send");

  const field = (id: string, name: string, el: HTMLInputElement | HTMLTextAreaElement) =>
    h("div", { class: "field" }, h("label", { for: id }, name), el);
  const to = h("input", { id: "to", type: "text", value: cur.to.join(", "), readonly: !(canEdit || canSend), autocomplete: "off" });
  const cc = h("input", { id: "cc", type: "text", value: cur.cc.join(", "), readonly: !(canEdit || canSend), autocomplete: "off" });
  const subject = h("input", { id: "subject", type: "text", value: cur.subject, readonly: !(canEdit || canSend), maxlength: 300 });
  const body = h("textarea", { id: "body", rows: 16, readonly: !(canEdit || canSend), maxlength: 20000 });
  body.value = cur.body;

  const result = h("div", { class: "result", "aria-live": "polite" });
  const edits = () => ({ draft_id: d.draftId, to: list(to.value), cc: list(cc.value), subject: subject.value, body: body.value });
  const busy = (on: boolean) => buttons.forEach((b) => (b.disabled = on));
  const act = async (tool: string, args: Record<string, unknown>, after?: (r: ActResult) => void) => {
    busy(true);
    try {
      const r = await api.tool<ActResult>(tool, args);
      const message = tool === "review_draft" ? "Saved. Not sent: press Send when it's ready."
        : tool === "discard_draft" ? "Discarded. It won't be sent."
        : r.decision === "REDIRECTED" ? "Sent, to the simulation recipients only, marked [SIM]. Nothing went to the recipients above."
        : r.decision === "DRAFTED" ? "Not sent: the outbound guard kept it as a draft for review."
        : "Sent.";
      replace(result, outcome(r, message));
      after?.(r);
    } catch (err) {
      replace(result, h("p", { class: "error" }, (err as ApiError).message));
    } finally {
      busy(false);
    }
  };
  // Sent or discarded: the draft is finished. The fields go read-only and the actions (buttons, the discard reason,
  // the note about sending) give way to its new status, as when the screen is opened on a finished draft.
  const done = (status: "sent" | "discarded") => (r: ActResult) => {
    if (r.status !== "APPLIED") return;
    [to, cc, subject, body].forEach((x) => (x.readOnly = true));
    actions.remove();
    replace(statusSlot, pill(status, status === "sent" ? "good" : "neutral"));
  };

  const sim = d.mode === "SIMULATION";
  const send = confirmButton("Send", sim ? "Press again to send (simulation recipients only)" : "Press again to send",
    () => void act("send_draft", edits(), done("sent")), "button-primary");
  const save = h("button", { type: "button", class: "button" }, "Save changes");
  save.addEventListener("click", () => void act("review_draft", edits()));
  const reason = h("input", { id: "reason", type: "text", placeholder: "Why it shouldn't go", maxlength: 500 });
  const discard = confirmButton("Discard", "Press again to discard",
    () => void act("discard_draft", { draft_id: d.draftId, reason: reason.value.trim() }, done("discarded")), "button-quiet", () => {
      if (reason.value.trim()) return true;
      replace(result, h("p", { class: "error" }, "Say why it shouldn't go, then discard."));
      return false;
    });
  const buttons = [canSend ? send : null, canEdit ? save : null, canEdit ? discard : null].filter((b): b is HTMLButtonElement => b !== null);

  const statusSlot = h("div", null, pending ? null : pill(d.status.toLowerCase(), d.status === "SENT" ? "good" : "neutral"));
  const actions = h("div", { class: "actions" },
    canSend ? send : null,
    canEdit ? save : null,
    canEdit ? h("div", { class: "field" }, h("label", { for: "reason" }, "Discard, with a reason"), reason, discard) : null,
    pending && !canEdit && !canSend ? pill("Read only: editing and sending need permissions you don't have", "neutral") : null,
    pending ? h("p", { class: "caption" }, "Sending goes through the outbound guard, which checks the recipients, the switch and the " +
      "blocked list again, and logs it under your name.") : null);

  const pdfReport = h("div", { "aria-live": "polite" });
  const pdf = d.vendorPo && can(me, "costs.read") ? h("button", { type: "button", class: "button button-small" }, `Open ${d.vendorPo}'s PDF`) : null;
  pdf?.addEventListener("click", () => openPdf(api, { vendor_po_number: d.vendorPo! }, pdfReport));

  return h("div", { class: "page" },
    order?.customerPoId && d.vendorPo
      ? crumbs(...orderCrumbs(order.customerPoId, order.poNumber), { text: d.vendorPo, href: `/vendor-po/${d.vendorPo}`, num: true }, { text: "Email" })
      : d.status === "PENDING"
        ? crumbs({ text: "Home", href: "/" }, { text: "Drafts to send" }, { text: `Email ${d.draftId}` })
        : crumbs({ text: "Mail", href: "/mail" }, { text: `Email ${d.draftId}` }),
    h("div", { class: "stack-tight" },
      label(`Email · ${d.purpose.toLowerCase().replace(/_/g, " ")} · ${d.status.toLowerCase()}`),
      h("h1", { class: "title" }, cur.subject),
      h("p", { class: "lede" }, d.whyDrafted ?? "", d.parties.length ? ` For ${d.parties.map((p) => p.name).join(", ")}.` : "")),
    sim ? h("div", { class: "notice" }, `Simulation: sending delivers only to ${me.simulationRecipients?.join(", ") || "the simulation recipients"}, with [SIM] in the subject.`) : null,
    h("div", { class: "layout" },
      h("div", { class: "main" },
        h("form", { class: "panel form", onsubmit: ((e: Event) => e.preventDefault()) as EventListener },
          h("div", { class: "row" }, h("span", { class: "label" }, "From"), h("span", { class: "num" }, d.from)),
          field("to", "To", to), field("cc", "Cc", cc), field("subject", "Subject", subject), field("body", "Message", body))),
      h("aside", { class: "side", "aria-label": "Decision" },
        h("div", { class: "panel decision" },
          statusSlot,
          actions,
          result),
        h("div", { class: "panel facts" },
          d.vendorPo
            ? [label("For"), h("span", null, h("a", { class: "num", href: `/vendor-po/${d.vendorPo}` }, d.vendorPo),
                order?.customerPoId ? [" on order ", h("a", { class: "num", href: `/order/${order.customerPoId}` }, order.poNumber)] : null)]
            : null,
          label("Attachments"),
          d.attachments.length ? h("ul", { class: "plain" }, d.attachments.map((a) => h("li", { class: "num" }, a))) : h("span", null, "None"),
          pdf, pdfReport,
          label("Drafted"),
          h("span", null, when(d.createdAt), ` by ${d.source}`),
          d.reviewedAt && !sentLog ? [label("Last edited"), h("span", null, `${when(d.reviewedAt)} by ${d.reviewedBy}`)] : null,
          sentLog ? [label("Sent"), h("span", null, `${when(sentLog.at)} by ${sentBy(sentLog.source)}`),
            label("Delivered to"), h("span", null, sentLog.deliveredTo.join(", ") || "nobody"),
            sentLog.decision === "REDIRECTED" ? h("span", { class: "caption" }, "Simulation: only these got it, marked [SIM].") : null] : null))));
}
